import { useCallback, useEffect, useState } from "react";
import {
  BASE_MAP_OPTIONS,
  BaseMapToolKey,
  BaseMapValue,
} from "../config/maps/baseMaps";
import {
  addHiddenColumn,
  getHiddenColumns,
  removeHiddenColumn,
} from "../utilities/functions";

const BASE_MAP_STORAGE_KEY = "preferred_base_map";
const BASE_MAP_PREFERENCE_PAGE_ID = 3;
const BASE_MAP_PREFERENCE_PREFIX = "base_map:";
const DEFAULT_BASE_MAP: BaseMapValue = "google_roadmap";

const validBaseMapValues = new Set<BaseMapValue>(
  BASE_MAP_OPTIONS.map((option) => option.value)
);

function isBaseMapValue(value: string | null | undefined): value is BaseMapValue {
  return !!value && validBaseMapValues.has(value as BaseMapValue);
}

function getStorageKey(toolKey: BaseMapToolKey) {
  return `${BASE_MAP_STORAGE_KEY}_${toolKey}`;
}

function readLocalBaseMap(toolKey: BaseMapToolKey): BaseMapValue {
  const storedValue =
    localStorage.getItem(getStorageKey(toolKey)) ||
    localStorage.getItem(BASE_MAP_STORAGE_KEY);
  return isBaseMapValue(storedValue) ? storedValue : DEFAULT_BASE_MAP;
}

function getPreferencePrefix(toolKey: BaseMapToolKey) {
  return `${BASE_MAP_PREFERENCE_PREFIX}${toolKey}:`;
}

function toPreferenceValue(toolKey: BaseMapToolKey, value: BaseMapValue) {
  return `${getPreferencePrefix(toolKey)}${value}`;
}

function readLegacyPreference(preferences: string[]) {
  const legacyPreference = preferences
    .find((preference) => isLegacyBaseMapPreference(preference));
  const legacyValue = legacyPreference
    ?.replace(BASE_MAP_PREFERENCE_PREFIX, "");

  return isBaseMapValue(legacyValue) ? legacyValue : null;
}

function isLegacyBaseMapPreference(preference: string) {
  if (!preference.startsWith(BASE_MAP_PREFERENCE_PREFIX)) return false;

  const legacyValue = preference.replace(BASE_MAP_PREFERENCE_PREFIX, "");
  return isBaseMapValue(legacyValue);
}

export function useBaseMapPreference(toolKey: BaseMapToolKey = "map") {
  const [baseMap, setBaseMapState] = useState<BaseMapValue>(() =>
    readLocalBaseMap(toolKey)
  );

  useEffect(() => {
    const idUser = Number(localStorage.getItem("userid") || 0);
    if (!idUser) return;

    let cancelled = false;

    async function loadPreference() {
      try {
        const preferences = await getHiddenColumns(
          idUser,
          BASE_MAP_PREFERENCE_PAGE_ID
        );
        const preferencePrefix = getPreferencePrefix(toolKey);
        const savedPreference = preferences.find((preference) =>
          preference.startsWith(preferencePrefix)
        );
        const savedBaseMap =
          savedPreference?.replace(preferencePrefix, "") ||
          readLegacyPreference(preferences);

        if (!cancelled && isBaseMapValue(savedBaseMap)) {
          localStorage.setItem(getStorageKey(toolKey), savedBaseMap);
          setBaseMapState(savedBaseMap);
        }
      } catch (error) {
        console.warn("Failed to load base map preference:", error);
      }
    }

    loadPreference();

    return () => {
      cancelled = true;
    };
  }, [toolKey]);

  const setBaseMap = useCallback(async (nextBaseMap: BaseMapValue) => {
    setBaseMapState(nextBaseMap);
    localStorage.setItem(getStorageKey(toolKey), nextBaseMap);

    const idUser = Number(localStorage.getItem("userid") || 0);
    if (!idUser) return;

    try {
      const preferences = await getHiddenColumns(
        idUser,
        BASE_MAP_PREFERENCE_PAGE_ID
      );
      const preferencePrefix = getPreferencePrefix(toolKey);
      const stalePreferences = preferences.filter((preference) =>
        preference.startsWith(preferencePrefix) ||
        (toolKey === "map" &&
          isLegacyBaseMapPreference(preference))
      );

      await Promise.all(
        stalePreferences
          .filter((preference) => preference !== toPreferenceValue(toolKey, nextBaseMap))
          .map((columnName) =>
            removeHiddenColumn({
              id_user: idUser,
              id_page: BASE_MAP_PREFERENCE_PAGE_ID,
              column_name: columnName,
            })
          )
      );

      await addHiddenColumn({
        id_user: idUser,
        id_page: BASE_MAP_PREFERENCE_PAGE_ID,
        column_name: toPreferenceValue(toolKey, nextBaseMap),
      });
    } catch (error) {
      console.warn("Failed to save base map preference:", error);
    }
  }, [toolKey]);

  return [baseMap, setBaseMap] as const;
}
